/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BACKSPACE, DELETE, SPACE } from '@angular/cdk/keycodes';
import { Directive, ElementRef, EventEmitter, Input, Output, Renderer2, } from '@angular/core';
import { mixinColor, mixinDisabled } from '@angular/material/core';
import { Subject } from 'rxjs/Subject';
/**
 * Event object emitted by MatChip when selected or deselected.
 */
export class MatChipSelectionChange {
    /**
     * @param {?} source
     * @param {?} selected
     * @param {?=} isUserInput
     */
    constructor(source, selected, isUserInput = false) {
        this.source = source;
        this.selected = selected;
        this.isUserInput = isUserInput;
    }
}
function MatChipSelectionChange_tsickle_Closure_declarations() {
    /** @type {?} */
    MatChipSelectionChange.prototype.source;
    /** @type {?} */
    MatChipSelectionChange.prototype.selected;
    /** @type {?} */
    MatChipSelectionChange.prototype.isUserInput;
}
/**
 * \@docs-private
 */
export class MatChipBase {
    /**
     * @param {?} _renderer
     * @param {?} _elementRef
     */
    constructor(_renderer, _elementRef) {
        this._renderer = _renderer;
        this._elementRef = _elementRef;
    }
}
function MatChipBase_tsickle_Closure_declarations() {
    /** @type {?} */
    MatChipBase.prototype._renderer;
    /** @type {?} */
    MatChipBase.prototype._elementRef;
}
export const /** @type {?} */ _MatChipMixinBase = mixinColor(mixinDisabled(MatChipBase), 'primary');
/**
 * Dummy directive to add CSS class to basic chips.
 * \@docs-private
 */
export class MatBasicChip {
}
MatBasicChip.decorators = [
    { type: Directive, args: [{
                selector: `mat-basic-chip, [mat-basic-chip]`,
                host: { 'class': 'mat-basic-chip' },
            },] },
];
/**
 * @nocollapse
 */
MatBasicChip.ctorParameters = () => [];
function MatBasicChip_tsickle_Closure_declarations() {
    /** @type {?} */
    MatBasicChip.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatBasicChip.ctorParameters;
}
/**
 * Material design styled Chip component. Used inside the MatChipList component.
 */
export class MatChip extends _MatChipMixinBase {
    /**
     * @param {?} renderer
     * @param {?} _elementRef
     */
    constructor(renderer, _elementRef) {
        super(renderer, _elementRef);
        this._elementRef = _elementRef;
        this._selected = false;
        this._selectable = true;
        this._removable = true;
        /**
         * Whether the chip has focus.
         */
        this._hasFocus = false;
        /**
         * Emits when the chip is focused.
         */
        this._onFocus = new Subject();
        /**
         * Emits when the chip is blured.
         */
        this._onBlur = new Subject();
        /**
         * Emitted when the chip is selected or deselected.
         */
        this.selectionChange = new EventEmitter();
        /**
         * Emitted when the chip is destroyed.
         */
        this.destroyed = new EventEmitter();
        /**
         * Emitted when the chip is destroyed.
         * @deprecated Use 'destroyed' instead.
         */
        this.destroy = this.destroyed;
        /**
         * Emitted when a chip is to be removed.
         */
        this.removed = new EventEmitter();
        /**
         * Emitted when a chip is to be removed.
         * @deprecated Use `removed` instead.
         */
        this.onRemove = this.removed;
    }
    /**
     * Whether the chip is selected.
     * @return {?}
     */
    get selected() {
        return this._selected;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set selected(value) {
        this._selected = coerceBooleanProperty(value);
        this.selectionChange.emit({
            source: this,
            isUserInput: false,
            selected: value
        });
    }
    /**
     * The value of the chip. Defaults to the content inside <mat-chip> tags.
     * @return {?}
     */
    get value() {
        return this._value != undefined
            ? this._value
            : this._elementRef.nativeElement.textContent;
    }
    /**
     * @param {?} newValue
     * @return {?}
     */
    set value(newValue) {
        this._value = newValue;
    }
    /**
     * Whether or not the chips are selectable. When a chip is not selectable,
     * changes to it's selected state are always ignored.
     * @return {?}
     */
    get selectable() {
        return this._selectable;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set selectable(value) {
        this._selectable = coerceBooleanProperty(value);
    }
    /**
     * Determines whether or not the chip displays the remove styling and emits (remove) events.
     * @return {?}
     */
    get removable() {
        return this._removable;
    }
    /**
     * @param {?} value
     * @return {?}
     */
    set removable(value) {
        this._removable = coerceBooleanProperty(value);
    }
    /**
     * @return {?}
     */
    get ariaSelected() {
        return this.selectable ? this.selected.toString() : null;
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroyed.emit({ chip: this });
    }
    /**
     * Selects the chip.
     * @return {?}
     */
    select() {
        this._selected = true;
        this.selectionChange.emit({
            source: this,
            isUserInput: false,
            selected: true
        });
    }
    /**
     * Deselects the chip.
     * @return {?}
     */
    deselect() {
        this._selected = false;
        this.selectionChange.emit({
            source: this,
            isUserInput: false,
            selected: false
        });
    }
    /**
     * Select this chip and emit selected event
     * @return {?}
     */
    selectViaInteraction() {
        this._selected = true;
        // Emit select event when selected changes.
        this.selectionChange.emit({
            source: this,
            isUserInput: true,
            selected: true
        });
    }
    /**
     * Toggles the current selected state of this chip.
     * @param {?=} isUserInput
     * @return {?}
     */
    toggleSelected(isUserInput = false) {
        this._selected = !this.selected;
        this.selectionChange.emit({
            source: this,
            isUserInput,
            selected: this._selected
        });
        return this.selected;
    }
    /**
     * Allows for programmatic focusing of the chip.
     * @return {?}
     */
    focus() {
        this._elementRef.nativeElement.focus();
        this._onFocus.next({ chip: this });
    }
    /**
     * Allows for programmatic removal of the chip. Called by the MatChipList when the DELETE or
     * BACKSPACE keys are pressed.
     *
     * Informs any listeners of the removal request. Does not remove the chip from the DOM.
     * @return {?}
     */
    remove() {
        if (this.removable) {
            this.removed.emit({ chip: this });
        }
    }
    /**
     * Ensures events fire properly upon click.
     * @param {?} event
     * @return {?}
     */
    _handleClick(event) {
        // Check disabled
        if (this.disabled) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.focus();
    }
    /**
     * Handle custom key presses.
     * @param {?} event
     * @return {?}
     */
    _handleKeydown(event) {
        if (this.disabled) {
            return;
        }
        switch (event.keyCode) {
            case DELETE:
            case BACKSPACE:
                // If we are removable, remove the focused chip
                this.remove();
                // Always prevent so page navigation does not occur
                event.preventDefault();
                break;
            case SPACE:
                // If we are selectable, toggle the focused chip
                if (this.selectable) {
                    this.toggleSelected(true);
                }
                // Always prevent space from scrolling the page since the list has focus
                event.preventDefault();
                break;
        }
    }
    /**
     * @return {?}
     */
    _blur() {
        this._hasFocus = false;
        this._onBlur.next({ chip: this });
    }
}
MatChip.decorators = [
    { type: Directive, args: [{
                selector: `mat-basic-chip, [mat-basic-chip], mat-chip, [mat-chip]`,
                inputs: ['color', 'disabled'],
                exportAs: 'matChip',
                host: {
                    'class': 'mat-chip',
                    'tabindex': '-1',
                    'role': 'option',
                    '[class.mat-chip-selected]': 'selected',
                    '[attr.disabled]': 'disabled || null',
                    '[attr.aria-disabled]': 'disabled.toString()',
                    '[attr.aria-selected]': 'ariaSelected',
                    '(click)': '_handleClick($event)',
                    '(keydown)': '_handleKeydown($event)',
                    '(focus)': '_hasFocus = true',
                    '(blur)': '_blur()',
                },
            },] },
];
/**
 * @nocollapse
 */
MatChip.ctorParameters = () => [
    { type: Renderer2, },
    { type: ElementRef, },
];
MatChip.propDecorators = {
    'selected': [{ type: Input },],
    'value': [{ type: Input },],
    'selectable': [{ type: Input },],
    'removable': [{ type: Input },],
    'selectionChange': [{ type: Output },],
    'destroyed': [{ type: Output },],
    'destroy': [{ type: Output },],
    'removed': [{ type: Output },],
    'onRemove': [{ type: Output, args: ['remove',] },],
};
function MatChip_tsickle_Closure_declarations() {
    /** @type {?} */
    MatChip.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatChip.ctorParameters;
    /** @type {?} */
    MatChip.propDecorators;
    /** @type {?} */
    MatChip.prototype._value;
    /** @type {?} */
    MatChip.prototype._selected;
    /** @type {?} */
    MatChip.prototype._selectable;
    /** @type {?} */
    MatChip.prototype._removable;
    /**
     * Whether the chip has focus.
     * @type {?}
     */
    MatChip.prototype._hasFocus;
    /**
     * Emits when the chip is focused.
     * @type {?}
     */
    MatChip.prototype._onFocus;
    /**
     * Emits when the chip is blured.
     * @type {?}
     */
    MatChip.prototype._onBlur;
    /**
     * Emitted when the chip is selected or deselected.
     * @type {?}
     */
    MatChip.prototype.selectionChange;
    /**
     * Emitted when the chip is destroyed.
     * @type {?}
     */
    MatChip.prototype.destroyed;
    /**
     * Emitted when the chip is destroyed.
     * @deprecated Use 'destroyed' instead.
     * @type {?}
     */
    MatChip.prototype.destroy;
    /**
     * Emitted when a chip is to be removed.
     * @type {?}
     */
    MatChip.prototype.removed;
    /**
     * Emitted when a chip is to be removed.
     * @deprecated Use `removed` instead.
     * @type {?}
     */
    MatChip.prototype.onRemove;
    /** @type {?} */
    MatChip.prototype._elementRef;
}
/**
 * Applies proper (click) support and adds styling for use with the Material Design "cancel" icon
 * available at https://material.io/icons/#ic_cancel.
 *
 * Example:
 *
 *     <mat-chip>
 *       <mat-icon matChipRemove>cancel</mat-icon>
 *     </mat-chip>
 *
 * You *may* use a custom icon, but you may need to override the `mat-chip-remove` positioning
 * styles to properly center the icon within the chip.
 */
export class MatChipRemove {
    /**
     * @param {?} _parentChip
     */
    constructor(_parentChip) {
        this._parentChip = _parentChip;
    }
    /**
     * Calls the parent chip's public `remove()` method if applicable.
     * @return {?}
     */
    _handleClick() {
        if (this._parentChip.removable) {
            this._parentChip.remove();
        }
    }
}
MatChipRemove.decorators = [
    { type: Directive, args: [{
                selector: '[matChipRemove]',
                host: {
                    'class': 'mat-chip-remove',
                    '(click)': '_handleClick($event)',
                },
            },] },
];
/**
 * @nocollapse
 */
MatChipRemove.ctorParameters = () => [
    { type: MatChip, },
];
function MatChipRemove_tsickle_Closure_declarations() {
    /** @type {?} */
    MatChipRemove.decorators;
    /**
     * @nocollapse
     * @type {?}
     */
    MatChipRemove.ctorParameters;
    /** @type {?} */
    MatChipRemove.prototype._parentChip;
}
//# sourceMappingURL=chip.js.map